// ModernIcons — Modern Layout 専用の inline SVG アイコン群 (新 dep を入れない、§9.6)。
//
// 方針 (docs/_wip/modern-layout.md §9.6「新 UI の視覚言語・アイコンセット (既存 deps
// で賄えるか)」): Classic の旧 .gif アイコンは「Classic っぽさ」を持ち込むので使わず、
// CSP / 一般ペイント風のクリーンな線アイコンを inline SVG で自前定義する。これにより
// アイコンライブラリ等の新規依存を増やさない。
//
// すべて 24x24 viewBox / currentColor stroke の単色線画。サイズ・色は呼び出し側が
// className (text-* / w-* h-*) で制御する。装飾目的なので aria-hidden、意味は
// 呼び出し側ボタンの aria-label / title が担う。

interface IconProps {
  readonly className?: string;
}

// **Tailwind の順序の罠 (2026-08-18)**: 既定サイズを `h-5 w-5 ${className}` のように
// **前置きしても、呼び出し側の `h-3` 等は勝てない**。Tailwind は class 属性の並び順では
// なく **CSS の生成順**で勝敗が決まり、`.h-3` は `.h-5` より前に出力されるため、常に
// 既定の `h-5` (20px) が勝つ。この形になっていた間、サイズを渡していた 6 箇所
// (レイヤーパネルの目玉 / パネルのグリップ / ブラシパネルのタブ) はすべて 20px で
// 描かれ、指定が無視されていた。
// → **呼び出し側が h-* / w-* を指定していたら既定を付けない**。h と w は独立に判定する
//   (片方だけ指定するケースを壊さないため)。
const base = (className?: string): { className: string } => {
  const cls = className ?? '';
  const hasH = /(^|\s)h-/.test(cls);
  const hasW = /(^|\s)w-/.test(cls);
  const defaults = [hasH ? '' : 'h-5', hasW ? '' : 'w-5'].filter(Boolean).join(' ');
  return { className: defaults === '' ? cls : `${defaults} ${cls}`.trim() };
};

/** ブラシ (筆): 斜めの筆体 + 穂先。 */
export const BrushIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 20c2.5 0 4-1.5 4-4l-1-1-3 3z" />
    <path d="M7 15 18 4a2 2 0 0 1 3 3L10 18z" />
  </svg>
);

/** 消しゴム。 */
export const EraserIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 20H5l-2-2a2 2 0 0 1 0-3l9-9 7 7-7 7" />
    <path d="m9 11 4 4" />
  </svg>
);

/** スポイト (eyedropper): 斜めのスポイト本体 + 先端の雫。 */
export const EyedropperIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* スポイトの吸い口 (上端の球) → 斜めの管 → 先端。 */}
    <path d="m19 3 2 2-3.5 3.5" />
    <path d="m15.5 6.5 2 2" />
    <path d="m17 8-8 8H5v-4l8-8z" />
  </svg>
);

/** 範囲 (点線の四角): 範囲を選んで移動・コピーする道具。 */
export const RegionIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="5" width="16" height="14" rx="1" strokeDasharray="3 3" />
  </svg>
);

/** 手のひら (pan)。 */
export const HandIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 12V5a1.5 1.5 0 0 1 3 0v6" />
    <path d="M11 11V4a1.5 1.5 0 0 1 3 0v7" />
    <path d="M14 11V6a1.5 1.5 0 0 1 3 0v8" />
    <path d="M8 12v-1a1.5 1.5 0 0 0-3 0v3c0 4 2.5 6 6 6a6 6 0 0 0 6-6" />
  </svg>
);

/** ズーム (虫眼鏡)。 */
export const ZoomIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx={10} cy={10} r={6} />
    <path d="m20 20-4.5-4.5" />
  </svg>
);

/** 全消し (ゴミ箱)。 */
export const TrashIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7h16" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/** キャプチャ (ダウンロード/保存)。 */
export const CaptureIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 19h14" />
  </svg>
);

/** 画像貼付。 */
export const ImageIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x={3} y={4} width={18} height={16} rx={2} />
    <circle cx={8.5} cy={9} r={1.5} />
    <path d="m4 17 5-5 4 4 3-3 4 4" />
  </svg>
);

/** 名前ラベル表示トグル (吹き出し)。 */
export const NameTagIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 5h16v10H10l-4 4v-4H4z" />
    <path d="M8 9h8M8 12h5" />
  </svg>
);

/** 左右反転 (鏡像)。中央の縦軸を挟んで両側に向き合う三角 + 軸の点線。 */
export const FlipHorizontalIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* 中央の対称軸 (点線)。 */}
    <path d="M12 3v18" strokeDasharray="2 2.5" />
    {/* 左の三角 (軸へ向かう)。 */}
    <path d="M9 6 4 12l5 6z" />
    {/* 右の三角 (軸へ向かう、鏡像)。 */}
    <path d="M15 6l5 6-5 6z" />
  </svg>
);

/** 回転 (時計回りの円弧矢印)。表示回転ツール / 角度表示用。 */
export const RotateIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 12a8 8 0 1 1-2.6-5.9" />
    <path d="M20 4v4.5h-4.5" />
  </svg>
);

/** 目玉 (表示)。 */
export const EyeIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7" />
    <circle cx={12} cy={12} r={3} />
  </svg>
);

/** ドラッグハンドル (= 横 2 本のグリップ線、§9.10 第4スライス パネル並べ替え)。 */
export const GripIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 8"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M5 3h14" />
    <path d="M5 6h14" />
  </svg>
);

/** タイムラプス (フィルム: 枠 + 送り穴 + 再生マーク)。共有入口 (T3) 用。 */
export const TimelapseFilmIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 4v16M17 4v16" />
    <path d="M3 8h4M3 12h4M3 16h4" />
    <path d="M17 8h4M17 12h4M17 16h4" />
    <path d="m10.5 9.5 3.5 2.5-3.5 2.5z" />
  </svg>
);

/** 過去キャンバス (履歴): 時計回りの巻き戻し矢印 + 時計。 */
export const HistoryIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 3v6h6" />
    <path d="M3.5 9a9 9 0 1 1-1 5" />
    <path d="M12 8v4l3 2" />
  </svg>
);

/** 元に戻す (Undo): 反時計回りの巻き戻し矢印。 */
export const UndoIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 7 4 12l5 5" />
    <path d="M4 12h10a6 6 0 0 1 0 12h-2" />
  </svg>
);

/**
 * リセット (初期値に戻す): ほぼ一周する反時計回りの矢印。`UndoIcon` (折り返し矢印) と
 * 見分けがつく形にする (太さプリセットの RESET、2026-09-29)。
 */
export const ResetIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

/**
 * マイブラシ (星): 「自分のもの」を表す記号。Modern ブラシパネルのタブバー
 * (`ModernBrushPanel.tsx`) で「マイブラシ」タブだけをアイコン化するのに使う
 * (2026-08-19、狭ドック 180px でタブ行が2段折返しする問題への対処。
 * 「線画/塗り/質感」はテキストラベルのまま — 理由は `ModernBrushPanel.tsx` の
 * `BRUSH_PANEL_TABS` 直上コメント参照)。
 */
export const MyBrushIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3.5 14.5 9l6 .9-4.3 4.2 1 6-5.2-2.8-5.2 2.8 1-6L3.5 9.9l6-.9z" />
  </svg>
);

/**
 * ヘルプ (丸囲み「?」): 使い方マニュアル入口の装飾アイコン (作者フィードバック
 * 2026-09-22「もっと目立つようにできないかな」への改修)。RoomHeader の「使い方」
 * ボタン (Modern ヘッダ) / `ModernPhoneShell` の「その他」シートの同ボタンで使う。
 * Classic はテキストのみのボタン美意識を保つため、この SVG は使わず "?" 文字を
 * ラベルに前置する (`ClassicLayout.tsx` 側の判断、`ModernIcons.tsx` 自体は Modern
 * 系レイアウト専用という冒頭コメントの方針どおり)。
 */
export const HelpIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx={12} cy={12} r={9} />
    <path d="M9.5 9a2.5 2.5 0 1 1 3.7 2.2c-1 .6-1.2 1.2-1.2 2.3" />
    <circle cx={12} cy={17.2} r={0.9} fill="currentColor" stroke="none" />
  </svg>
);

/**
 * 招待 (人 + プラス): 部屋 URL を友達に渡す「招待」ボタンの装飾アイコン
 * (room-invite-2026-10-01.md)。Modern ヘッダ / ModernPhoneShell「その他」で使う。
 */
export const InviteIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx={10} cy={8} r={3.5} />
    <path d="M3.5 19.5c.6-3.3 3.2-5 6.5-5s5.9 1.7 6.5 5" />
    <path d="M19 6v6M16 9h6" />
  </svg>
);

/** 目玉に斜線 (非表示)。 */
export const EyeOffIcon = ({ className }: IconProps): React.ReactNode => (
  <svg
    {...base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 3l18 18" />
    <path d="M10.6 6.1A9.7 9.7 0 0 1 12 6c6.5 0 10 6 10 6a16 16 0 0 1-3 3.5" />
    <path d="M6.6 6.6A16 16 0 0 0 2 12s3.5 6 10 6a9.5 9.5 0 0 0 4-.9" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);
